type ScheduleOptions = {
    priority: 'background' | 'user-visible' | 'user-blocking';
    delay?: number;
    signal?: AbortSignal;
};

export function schedule<T extends () => any>(
    task: T,
    options?: number | ScheduleOptions
): Promise<Awaited<ReturnType<T>>> {
    const isSimpleOption = typeof options === 'number' || typeof options === 'undefined';
    const delay = isSimpleOption ? options : options?.delay;
    const priority = isSimpleOption ? 'background' : options?.priority;
    const signal = isSimpleOption ? undefined : options?.signal;

    if (typeof self.scheduler?.postTask === 'function') {
        return self.scheduler.postTask(task, {
            priority,
            delay,
            signal,
        });
    } else {
        return new Promise((resolve, reject) => {
            const useIdleCallback =
                typeof delay === 'undefined' &&
                priority === 'background' &&
                typeof self.requestIdleCallback === 'function';

            let timer: number;
            if (signal) {
                if (signal.aborted) {
                    reject(signal.reason);
                    return;
                }

                signal.addEventListener('abort', abort);

                function abort() {
                    if (useIdleCallback) {
                        self.cancelIdleCallback(timer);
                    } else {
                        self.clearTimeout(timer);
                    }
                    reject(signal?.reason);
                    signal?.removeEventListener('abort', abort);
                }
            }
            if (useIdleCallback) {
                timer = self.requestIdleCallback(completer);
            } else {
                timer = self.setTimeout(completer, delay);
            }

            function completer() {
                if (signal?.aborted) {
                    reject(signal.reason);
                    return;
                }
                // we need to wrap the task in a try-catch block to ensure that it throws inside of the promise
                // otherwise setTimeout will throw to the global scope
                try {
                    resolve(task());
                } catch (e) {
                    reject(e);
                }
            }
        });
    }
}

export function yieldNow() {
    if (typeof self.scheduler?.yield === 'function') {
        return self.scheduler.yield();
    }
    return new Promise(resolve => self.setTimeout(resolve));
}

export function yieldIdle() {
    if (self.requestIdleCallback) {
        return new Promise(resolve => self.requestIdleCallback(resolve));
    }

    return yieldNow();
}
